import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function About() {
  useEffect(() => {
    document.title = '关于我们 – JavBus 使用指南';
  }, []);

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'JavBus 使用指南',
    url: 'https://javbus-guide.com/',
    description: '致力于为中文用户提供 JavBus 影片资料库平台的完整使用指南。',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'JavBus 使用指南是什么？', acceptedAnswer: { '@type': 'Answer', text: 'JavBus 使用指南是一个致力于为中文用户提供 JavBus 影片资料库平台完整使用教程的信息导航站点，涵盖访问入口、搜索技巧、使用教程和常见问题等。' } },
      { '@type': 'Question', name: '本网站是否与 JavBus 官方有关联？', acceptedAnswer: { '@type': 'Answer', text: '本网站为独立的第三方信息导航站点，与 JavBus 官方平台无直接关联。我们仅提供平台使用相关的指导信息，不托管或存储任何影片内容。' } },
      { '@type': 'Question', name: '如何联系我们？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过本页面的联系表单提交信息，我们的团队会及时查看并回复您的问题或建议。' } },
    ],
  };

  const milestones = [
    { year: '2018', title: '平台成立', desc: '开始关注 JavBus 平台生态，着手整理使用指南与教程资料。' },
    { year: '2020', title: '内容扩展', desc: '扩展至五大内容分类，覆盖访问入口、搜索技巧、使用教程、镜像站点与常见问题。' },
    { year: '2022', title: '用户增长', desc: '月均访问量突破百万，成为中文用户首选的 JavBus 平台使用参考资源。' },
    { year: '2024', title: '全面升级', desc: '全新改版上线，优化阅读体验与搜索功能，持续为用户提供高质量内容。' },
    { year: '2026', title: '持续深耕', desc: '不断更新最新地址与教程内容，致力于成为最权威的指南站点。' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <div className="py-8 md:py-12 text-center">
              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                关于我们
              </h1>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                了解 JavBus 使用指南的品牌故事、发展历程与使命愿景
              </p>
            </div>
          </div>
        </div>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div className="animate-fade-in-up">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  品牌介绍
                </span>
                <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                  您的 JavBus 平台使用指南专家
                </h2>
                <div className="space-y-4 text-foreground-600 text-sm leading-relaxed">
                  <p>
                    JavBus 使用指南诞生于 2018 年，是一群热爱影片资料检索的平台用户共同创建的信息导航站点。
                    我们深知在海量影片资料中快速找到所需内容的挑战，因此决定将积累的平台使用经验系统化整理，
                    为中文用户提供最全面、最实用的 JavBus 使用教程。
                  </p>
                  <p>
                    经过多年的持续运营与内容迭代，我们已发展成为涵盖访问入口、搜索技巧、使用教程、
                    镜像站点和常见问题五大版块的综合性指南平台。我们的内容由资深用户撰写并定期审核更新，
                    确保每一条信息的准确性和时效性。
                  </p>
                  <p>
                    我们的使命是：<strong>让每一位 JavBus 用户都能轻松上手、高效检索，畅享完整的平台使用体验。</strong>
                    无论您是初次接触的新用户，还是希望提升搜索效率的资深用户，都能在这里找到所需的信息。
                  </p>
                </div>
              </div>
              <div className="animate-slide-in-right">
                <div className="bg-white rounded-lg p-6 md:p-8">
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-5">核心数据</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { value: '7+', label: '年持续运营' },
                      { value: '50+', label: '篇专业教程' },
                      { value: '100万+', label: '月均访问' },
                      { value: '5', label: '内容分类版块' },
                    ].map((stat, i) => (
                      <div key={i} className="text-center p-4 rounded-lg bg-background-100">
                        <div className="text-xl md:text-2xl font-heading font-bold text-accent-600 mb-1">
                          {stat.value}
                        </div>
                        <div className="text-foreground-500 text-xs">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                发展历程
              </span>
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                我们的成长之路
              </h2>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((m, i) => (
                  <div
                    key={i}
                    className={`relative flex flex-col md:flex-row gap-6 ${
                      i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`md:w-1/2 ${i % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                      <div className="bg-white rounded-lg p-5 ml-10 md:ml-0">
                        <span className="text-accent-600 font-heading font-bold text-lg">{m.year}</span>
                        <h3 className="font-heading font-semibold text-foreground-900 text-sm mt-1 mb-1.5">{m.title}</h3>
                        <p className="text-foreground-500 text-xs leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-2.5 h-2.5 rounded-full bg-accent-500 ring-4 ring-background-100 -translate-x-1/2 mt-5"></div>
                    <div className="md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-6 max-w-2xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                联系我们
              </span>
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                与我们取得联系
              </h2>
              <p className="text-foreground-600 text-sm max-w-md mx-auto">
                如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm mb-1">电子邮箱</h3>
                    <p className="text-foreground-500 text-xs">contact@javbus-guide.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-message-2-line text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm mb-1">在线留言</h3>
                    <p className="text-foreground-500 text-xs">使用下方表单，我们会在24小时内回复</p>
                  </div>
                </div>
              </div>

              <form
                data-readdy-form
                action="https://readdy.ai/api/form/d9rc2tvnahb7382ovkag"
                method="POST"
                className="space-y-4"
                onSubmit={(e) => {
                  const target = e.currentTarget;
                  const hpField = target.querySelector<HTMLInputElement>('[name="phone_alt"]');
                  if (hpField && hpField.value.trim()) {
                    e.preventDefault();
                    const statusEl = document.getElementById('form-status');
                    if (statusEl) {
                      statusEl.textContent = '感谢您的留言，我们会尽快回复！';
                      statusEl.className = 'text-sm text-center p-3 rounded-md bg-green-50 text-green-700';
                    }
                    return;
                  }
                }}
              >
                <input type="text" name="phone_alt" className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的姓名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱 <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
                </div>

                <button
                  type="submit"
                  className="whitespace-nowrap w-full py-3 rounded-md bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  提交留言
                </button>

                <div id="form-status"></div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}